<template>
  <div class="card card-custom card-shadowless border mb-5">
    <div class="card-header border-0 py-4">
      <div class="card-title align-items-center">
        <span class="card-label font-weight-bolder text-dark">
          <i class="fa fa-stamp mr-2 text-primary"></i>
          Stamp commercial terms
        </span>
      </div>
      <div class="card-toolbar">
        <b-badge :variant="statusBadgeVariant" class="font-weight-bold p-2">
          {{ statusBadgeLabel }}
        </b-badge>
      </div>
    </div>

    <div class="card-body pt-2 pb-5">
      <div v-if="identityLine" class="font-size-sm text-dark mb-3">
        {{ identityLine }}
      </div>

      <div v-if="loading" class="text-muted font-size-sm">
        <i class="fa fa-spinner fa-spin mr-1"></i> Checking commercial terms…
      </div>

      <template v-else>
        <!-- Bound / stamped -->
        <div v-if="status && status.status === 'bound'" class="font-size-sm text-success mb-3">
          <i class="fa fa-check-circle"></i>
          Bound under {{ status.policyNumber || carrierNumber }}
          <span v-if="hashPrefix"> · hash {{ hashPrefix }}</span>
        </div>

        <div v-else-if="!carrierNumber" class="font-size-sm text-muted mb-3">
          Fuse policy number (FUSE-*) is missing. Stamp and Schedule Generate stay closed.
        </div>

        <!-- Review needed: manual pricing reference -->
        <template v-else-if="isReviewNeeded">
          <div class="font-size-sm text-dark mb-3">
            The customer was quoted
            <span class="font-weight-bold">{{ quotedPremiumLabel }}</span>
            with a
            <span class="font-weight-bold">{{ quotedExcessLabel }}</span>
            excess. We could not find one rating record containing both values.
            Review how these terms were prepared before stamping them.
          </div>
          <div
            v-if="unusableRuns.length"
            class="font-size-sm text-muted mb-3"
          >
            Ignored {{ unusableRuns.length }} rating file{{ unusableRuns.length === 1 ? '' : 's' }}
            that cannot contain the quoted excess and premium.
            <div
              v-for="run in unusableRuns"
              :key="run.sourceRatingRunId || run.reason"
              class="mt-1"
            >
              {{ run.sourceRatingRunId || 'unknown run' }} — {{ run.reason }}
            </div>
          </div>

          <div v-if="!reviewOpen">
            <button
              type="button"
              class="btn btn-primary font-weight-bold text-uppercase mr-3 mb-2"
              :disabled="busy"
              @click.prevent="openReview"
            >
              Review quoted terms
            </button>
            <button
              type="button"
              class="btn btn-sm btn-light-primary font-weight-bold mb-2"
              :disabled="busy"
              @click.prevent="loadStatus"
            >
              <i class="fa fa-sync" :class="{ 'fa-spin': loading }"></i>
              Refresh
            </button>
          </div>

          <div v-else class="border rounded p-4 mb-3 bg-light">
            <h6 class="font-weight-bolder text-dark mb-3">
              Confirm how the quoted terms were prepared
            </h6>

            <div class="mb-4">
              <div class="font-weight-bold text-dark mb-2">
                Terms that will appear on the schedule
              </div>
              <div class="font-size-sm text-dark">
                Excess: {{ quotedExcessLabel }}
              </div>
              <div class="font-size-sm text-dark">
                Annual premium: {{ quotedPremiumLabel }}
              </div>
            </div>

            <div class="mb-4">
              <div class="font-weight-bold text-dark mb-2">
                Which rating record was used as the pricing reference?
              </div>
              <div class="font-size-sm text-muted mb-2">
                Rows are numbered. Stamp will refuse a rating whose vehicle count
                does not match the transaction fleet.
                This choice will not change the quoted terms.
              </div>
              <div
                v-if="transactionVehicleCountLabel"
                class="font-size-sm text-dark mb-2"
              >
                Transaction fleet: {{ transactionVehicleCountLabel }}.
              </div>
              <div
                v-if="suggestedCandidateLine"
                class="font-size-sm text-dark mb-3"
              >
                Suggested: {{ suggestedCandidateLine }}
              </div>
              <div
                v-for="candidate in numberedCandidates"
                :key="candidate.sourceRatingRunId"
                class="custom-control custom-radio mb-3"
              >
                <input
                  :id="'ref-' + candidate.sourceRatingRunId"
                  v-model="selectedReferenceRunId"
                  type="radio"
                  class="custom-control-input"
                  :value="candidate.sourceRatingRunId"
                  :disabled="stamping"
                />
                <label
                  class="custom-control-label font-size-sm text-dark"
                  :for="'ref-' + candidate.sourceRatingRunId"
                >
                  <span class="font-weight-bolder">#{{ candidate.displayNumber }}</span>
                  {{ formatCandidateLabel(candidate) }}
                  <span class="text-muted"> — {{ candidate.comparison }}</span>
                  <span
                    class="d-block mt-1"
                    :class="vehicleAlignmentClass(candidate)"
                  >
                    {{ formatVehicleAlignment(candidate) }}
                  </span>
                  <span
                    v-if="candidate.recommended"
                    class="badge badge-primary mt-1"
                  >
                    Suggested
                  </span>
                </label>
              </div>
              <div
                v-if="selectedMisalignedWarning"
                class="alert alert-warning font-size-sm mb-0"
              >
                {{ selectedMisalignedWarning }}
              </div>
              <div v-if="!numberedCandidates.length" class="font-size-sm text-warning">
                No readable rating records were found for this transaction.
              </div>
            </div>

            <div class="form-group mb-3">
              <label class="font-weight-bold text-dark">How were these terms prepared?</label>
              <select
                v-model="reasonPrepared"
                class="form-control"
                :disabled="stamping"
              >
                <option value="">Select…</option>
                <option value="outside_system_quote_honoured">
                  Quoted outside this system using an existing rating as the pricing reference
                </option>
              </select>
            </div>

            <div class="form-group mb-3">
              <label class="font-weight-bold text-dark" for="stamp-explanation">
                Why are the quoted terms different from the selected rating record?
              </label>
              <textarea
                id="stamp-explanation"
                v-model="explanation"
                class="form-control"
                rows="4"
                maxlength="2000"
                :disabled="stamping"
                placeholder="Write a short explanation of how these terms were prepared."
              ></textarea>
              <small class="form-text text-muted">
                This explanation is saved as evidence on the quote when you stamp.
              </small>
            </div>

            <div class="custom-control custom-checkbox mb-4">
              <input
                id="confirm-quoted-terms"
                v-model="confirmedQuotedTerms"
                type="checkbox"
                class="custom-control-input"
                :disabled="stamping"
              />
              <label class="custom-control-label font-size-sm text-dark" for="confirm-quoted-terms">
                I confirm these are the terms promised to the customer. The rating
                record selected above is supporting evidence only.
              </label>
            </div>

            <details class="mb-4">
              <summary class="font-size-sm text-muted">Technical details</summary>
              <div class="font-size-sm text-muted mt-2">
                <div v-if="status.quoteId">Quote ID: {{ status.quoteId }}</div>
                <div v-if="status.quoteMoneySha256">
                  Quote money hash: {{ String(status.quoteMoneySha256).slice(0, 12) }}…
                </div>
                <div v-if="selectedReferenceRunId">
                  Selected rating record: {{ selectedReferenceRunId }}
                </div>
              </div>
            </details>

            <div
              v-if="reconciliationRequired"
              class="alert alert-warning mb-4"
            >
              <div class="font-weight-bold text-dark mb-2">
                This policy has an early system record that does not match the accepted quote.
              </div>
              <div class="font-size-sm text-dark mb-3">
                An authorised operator must replace that record before you continue. The old
                record will stay available for audit, but it will no longer be used for current
                processing.
              </div>
              <div class="custom-control custom-checkbox mb-3">
                <input
                  id="confirm-no-external-bordereaux"
                  v-model="confirmedNoExternalBordereaux"
                  type="checkbox"
                  class="custom-control-input"
                  :disabled="busy"
                />
                <label
                  class="custom-control-label font-size-sm text-dark"
                  for="confirm-no-external-bordereaux"
                >
                  I confirm this policy has not been posted to INSIGHT or sent in an external
                  bordereaux.
                </label>
              </div>
              <button
                type="button"
                class="btn btn-warning font-weight-bold text-uppercase"
                :disabled="busy || !confirmedNoExternalBordereaux"
                @click.prevent="promptEarlyPolicyReconciliation"
              >
                <i class="fa fa-sync" :class="{ 'fa-spin': reconciling }"></i>
                {{ reconciling ? "Replacing and stamping..." : "Replace early record and continue" }}
              </button>
            </div>

            <div class="d-flex flex-wrap align-items-center">
              <button
                v-if="!reconciliationRequired"
                type="button"
                class="btn btn-primary font-weight-bold text-uppercase mr-3 mb-2"
                :disabled="!canConfirmStamp"
                @click.prevent="promptConfirmQuotedTerms"
              >
                <i class="fa fa-stamp" :class="{ 'fa-spin': stamping }"></i>
                {{ stamping ? "Stamping…" : "Confirm quoted terms and stamp" }}
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light font-weight-bold mb-2 mr-2"
                :disabled="stamping"
                @click.prevent="closeReview"
              >
                Back
              </button>
              <button
                type="button"
                class="btn btn-sm btn-light-primary font-weight-bold mb-2"
                :disabled="busy"
                @click.prevent="loadStatus"
              >
                Refresh details
              </button>
            </div>
          </div>
        </template>

        <!-- Ready / missing exact path -->
        <template v-else>
          <div v-if="stampLineSummary" class="font-size-sm text-dark mb-2">
            <span class="font-weight-bold">Quoted terms:</span> {{ stampLineSummary }}
          </div>
          <div
            v-if="status && status.stampLine && status.stampLine.selection === 'accepted_quote_excess_and_payable'"
            class="font-size-sm text-muted mb-3"
          >
            The quote and rating record agree.
          </div>
          <div v-else class="font-size-sm text-muted mb-3">
            Commercial terms are not stamped yet for this Fuse policy and period.
          </div>

          <div class="d-flex flex-wrap align-items-center">
            <button
              type="button"
              class="btn btn-primary font-weight-bold text-uppercase mr-3 mb-2"
              :disabled="!canStamp"
              @click.prevent="promptStamp"
            >
              <i class="fa fa-stamp" :class="{ 'fa-spin': stamping }"></i>
              {{ stamping ? "Stamping…" : "Review and stamp" }}
            </button>
            <button
              type="button"
              class="btn btn-sm btn-light-primary font-weight-bold mb-2"
              :disabled="busy"
              @click.prevent="loadStatus"
            >
              <i class="fa fa-sync" :class="{ 'fa-spin': loading }"></i>
              Refresh
            </button>
          </div>
        </template>

        <b-alert v-if="error" variant="warning" show class="mt-4 mb-0">
          <div class="text-dark">{{ error }}</div>
        </b-alert>
        <b-alert v-else-if="info" variant="success" show class="mt-4 mb-0">
          <div class="text-dark">{{ info }}</div>
        </b-alert>
      </template>
    </div>
  </div>
</template>

<script>
import {
  getCommercialTermsStampStatus,
  stampCommercialTerms,
  reconcileEarlyPolicy
} from "@/services/workbench-service";
import { mapStampCommercialTermsError } from "./map-stamp-commercial-terms-error";

function centsToMoneyLabel(cents) {
  const n = Number(cents);
  if (!Number.isSafeInteger(n)) return "—";
  return `$${(n / 100).toFixed(2)}`;
}

export default {
  name: "CommercialTermsStampRail",

  props: {
    ledgerRowId: {
      type: String,
      required: true
    },
    carrierNumber: {
      type: String,
      default: ""
    },
    workflowName: {
      type: String,
      default: ""
    },
    premiumCents: {
      type: Number,
      default: null
    }
  },

  data: function() {
    return {
      loading: false,
      stamping: false,
      reconciling: false,
      status: null,
      error: null,
      info: null,
      statusRequestLedgerRowId: null,
      statusRequestSeq: 0,
      reviewOpen: false,
      selectedReferenceRunId: "",
      reasonPrepared: "",
      explanation: "",
      confirmedQuotedTerms: false,
      confirmedNoExternalBordereaux: false,
      reconciliationRequired: null,
      attestedReviewEvidenceKey: ""
    };
  },

  mounted: function() {
    this.loadStatus();
  },

  watch: {
    ledgerRowId: function() {
      this.status = null;
      this.error = null;
      this.info = null;
      this.loading = false;
      this.stamping = false;
      this.reconciling = false;
      this.statusRequestLedgerRowId = null;
      this.resetReviewForm();
      this.loadStatus();
    }
  },

  computed: {
    busy: function() {
      return this.loading || this.stamping || this.reconciling;
    },
    isReviewNeeded: function() {
      return Boolean(this.status && this.status.status === "review_needed");
    },
    candidates: function() {
      return Array.isArray(this.status && this.status.candidates)
        ? this.status.candidates
        : [];
    },
    numberedCandidates: function() {
      return this.candidates.map(function(row, index) {
        const displayNumber = Number(row && row.displayNumber);
        return Object.assign({}, row, {
          displayNumber: Number.isSafeInteger(displayNumber) && displayNumber > 0
            ? displayNumber
            : index + 1
        });
      });
    },
    transactionVehicleCountLabel: function() {
      const fromStatus = Number(this.status && this.status.transactionVehicleCount);
      if (Number.isSafeInteger(fromStatus) && fromStatus > 0) {
        return fromStatus === 1 ? "1 vehicle" : fromStatus + " vehicles";
      }
      const fromRow = this.numberedCandidates.find(function(row) {
        return Number.isSafeInteger(Number(row.transactionVehicleCount))
          && Number(row.transactionVehicleCount) > 0;
      });
      const count = fromRow ? Number(fromRow.transactionVehicleCount) : null;
      if (!Number.isSafeInteger(count) || count <= 0) return "";
      return count === 1 ? "1 vehicle" : count + " vehicles";
    },
    suggestedCandidateLine: function() {
      const suggested = this.numberedCandidates.find(function(row) {
        return row && row.recommended === true;
      });
      if (!suggested) return "";
      return (
        "#" +
        suggested.displayNumber +
        " — " +
        this.formatCandidateLabel(suggested) +
        ". Vehicle count matches the transaction."
      );
    },
    selectedMisalignedWarning: function() {
      const selected = this.numberedCandidates.find(function(row) {
        return String((row && row.sourceRatingRunId) || "") ===
          String(this.selectedReferenceRunId || "");
      }.bind(this));
      if (!selected || selected.vehicleCountAligned !== false) return "";
      const ratingCount = Number(selected.ratingVehicleCount);
      const txnCount = Number(
        selected.transactionVehicleCount != null
          ? selected.transactionVehicleCount
          : this.status && this.status.transactionVehicleCount
      );
      const ratingText = Number.isSafeInteger(ratingCount)
        ? String(ratingCount)
        : "an unread count";
      const txnText = Number.isSafeInteger(txnCount)
        ? String(txnCount)
        : "a different count";
      return (
        "#" +
        selected.displayNumber +
        " has " +
        ratingText +
        " vehicles. The transaction has " +
        txnText +
        ". Stamp will refuse this choice. Pick a numbered row marked as matching."
      );
    },
    unusableRuns: function() {
      return Array.isArray(this.status && this.status.unusableRuns)
        ? this.status.unusableRuns
        : [];
    },
    quotedExcessLabel: function() {
      const cents =
        (this.status && this.status.acceptedExcessCents) != null
          ? this.status.acceptedExcessCents
          : this.status &&
            this.status.stampLine &&
            this.status.stampLine.acceptedExcessCents;
      return centsToMoneyLabel(cents);
    },
    quotedPremiumLabel: function() {
      const cents =
        this.status && Number.isSafeInteger(Number(this.status.boundPremiumCents))
          ? Number(this.status.boundPremiumCents)
          : null;
      return centsToMoneyLabel(cents);
    },
    identityLine: function() {
      const fuse = String(this.carrierNumber || "").trim();
      const workflow = String(this.workflowName || "").trim();
      if (fuse && workflow && fuse !== workflow) {
        return `Workflow ${workflow} · Policy ${fuse}`;
      }
      if (fuse) return `Policy ${fuse}`;
      if (workflow) return `Workflow ${workflow}`;
      return "";
    },
    hashPrefix: function() {
      const hash =
        (this.status && this.status.contentHash) ||
        (this.status && this.status.snapshotHash) ||
        "";
      const text = String(hash || "").trim();
      return text ? text.slice(0, 12) : "";
    },
    stampLineSummary: function() {
      const line = this.status && this.status.stampLine;
      if (!line || typeof line !== "object") return "";
      const parts = [];
      if (Number.isSafeInteger(Number(line.acceptedExcessCents))) {
        parts.push(`${centsToMoneyLabel(line.acceptedExcessCents)} excess`);
      }
      if (Number.isSafeInteger(Number(line.acceptedPremiumCents))) {
        parts.push(`${centsToMoneyLabel(line.acceptedPremiumCents)} annual premium`);
      }
      return parts.join(" · ");
    },
    statusBadgeLabel: function() {
      if (this.loading) return "Checking";
      if (this.error && !(this.status && this.status.status === "bound")) return "Error";
      if (this.status && this.status.status === "bound") {
        return this.hashPrefix ? `Stamped · ${this.hashPrefix}` : "Stamped";
      }
      if (this.status && this.status.status === "review_needed") return "Review needed";
      if (this.status && this.status.status === "incomplete") {
        return this.canStamp
          ? "Incomplete · re-stamp for schedule"
          : "Incomplete · refresh source";
      }
      if (this.status && this.status.status === "blocked") return "Blocked";
      if (this.status && this.status.status === "error") return "Error";
      if (this.canStamp) return "Ready";
      return "Missing";
    },
    statusBadgeVariant: function() {
      if (this.loading) return "light";
      if (this.error && !(this.status && this.status.status === "bound")) {
        return "light-warning";
      }
      if (this.status && this.status.status === "bound") return "light-success";
      if (this.status && this.status.status === "review_needed") return "light-warning";
      if (
        this.status &&
        (this.status.status === "error" ||
          this.status.status === "blocked" ||
          this.status.status === "incomplete")
      ) {
        return "light-warning";
      }
      if (this.canStamp) return "light-primary";
      return "light-warning";
    },
    canStamp: function() {
      if (this.busy) return false;
      if (!String(this.carrierNumber || "").trim()) return false;
      if (!this.status || this.status.canStamp !== true) return false;
      if (this.status.status === "bound") return false;
      if (this.status.status === "review_needed") return false;
      return true;
    },
    canConfirmStamp: function() {
      if (this.busy) return false;
      if (!this.isReviewNeeded) return false;
      if (this.status && this.status.canConfirmQuotedTerms === false) return false;
      if (!String(this.carrierNumber || "").trim()) return false;
      if (!String(this.selectedReferenceRunId || "").trim()) return false;
      if (this.reasonPrepared !== "outside_system_quote_honoured") return false;
      if (!String(this.explanation || "").trim()) return false;
      if (this.confirmedQuotedTerms !== true) return false;
      return true;
    }
  },

  methods: {
    resetReviewForm: function() {
      this.reviewOpen = false;
      this.selectedReferenceRunId = "";
      this.reasonPrepared = "";
      this.explanation = "";
      this.confirmedQuotedTerms = false;
      this.confirmedNoExternalBordereaux = false;
      this.reconciliationRequired = null;
      this.attestedReviewEvidenceKey = "";
    },

    reviewEvidenceKey: function(status) {
      const candidates = Array.isArray(status && status.candidates)
        ? status.candidates
        : [];
      const candidateKey = candidates
        .map(function(row) {
          return [
            row && (row.sourceRatingRunId || row.ratingRunId || row.id),
            row && row.excessCents,
            row && row.payableCents,
            row && row.createdTime
          ].join(":");
        })
        .join("|");
      return [String((status && status.quoteMoneySha256) || ""), candidateKey].join("#");
    },

    openReview: function() {
      this.reviewOpen = true;
      this.error = null;
      if (!String(this.selectedReferenceRunId || "").trim()) {
        const suggested = this.numberedCandidates.find(function(row) {
          return row && row.recommended === true && row.sourceRatingRunId;
        });
        if (suggested) {
          this.selectedReferenceRunId = String(suggested.sourceRatingRunId);
        }
      }
    },

    closeReview: function() {
      this.reviewOpen = false;
    },

    formatCandidateLabel: function(candidate) {
      const excess = centsToMoneyLabel(candidate && candidate.excessCents);
      const premium = centsToMoneyLabel(candidate && candidate.payableCents);
      const when = candidate && candidate.createdTime
        ? String(candidate.createdTime)
        : "";
      const base = `${excess} excess · ${premium} annual premium`;
      return when ? `${base} (${when})` : base;
    },

    formatVehicleAlignment: function(candidate) {
      const ratingCount = Number(candidate && candidate.ratingVehicleCount);
      const txnCount = Number(
        candidate && candidate.transactionVehicleCount != null
          ? candidate.transactionVehicleCount
          : this.status && this.status.transactionVehicleCount
      );
      if (candidate && candidate.vehicleCountAligned === true) {
        return ratingCount +
          (ratingCount === 1 ? " vehicle, matches transaction" : " vehicles, matches transaction");
      }
      if (candidate && candidate.vehicleCountAligned === false) {
        const ratingText = Number.isSafeInteger(ratingCount)
          ? String(ratingCount)
          : "unread";
        const txnText = Number.isSafeInteger(txnCount) ? String(txnCount) : "unknown";
        return (
          ratingText +
          " vehicles, does not match (transaction has " +
          txnText +
          ")"
        );
      }
      if (Number.isSafeInteger(ratingCount) && ratingCount > 0) {
        return ratingCount +
          (ratingCount === 1 ? " vehicle on this rating" : " vehicles on this rating");
      }
      return "Vehicle count unread on this rating";
    },

    vehicleAlignmentClass: function(candidate) {
      if (candidate && candidate.vehicleCountAligned === true) return "text-success";
      if (candidate && candidate.vehicleCountAligned === false) return "text-warning";
      return "text-muted";
    },

    buildPricingDecision: function() {
      const candidates = Array.isArray(this.status && this.status.candidates)
        ? this.status.candidates
        : [];
      const selected = candidates.find(function(row) {
        return String((row && row.sourceRatingRunId) || "") ===
          String(this.selectedReferenceRunId || "");
      }.bind(this));
      return {
        decisionMode: "manual_override",
        sourceQuoteId: String((this.status && this.status.quoteId) || ""),
        quoteMoneySha256: String((this.status && this.status.quoteMoneySha256) || ""),
        referenceRatingRunId: String(this.selectedReferenceRunId || ""),
        referenceExcessCents: selected && Number.isSafeInteger(Number(selected.excessCents))
          ? Number(selected.excessCents)
          : null,
        referencePremiumCents: selected && Number.isSafeInteger(Number(selected.payableCents))
          ? Number(selected.payableCents)
          : null,
        reasonCode: "outside_system_quote_honoured",
        explanation: String(this.explanation || "").trim(),
        confirmedQuotedTerms: true
      };
    },

    loadStatus: function() {
      const id = String(this.ledgerRowId || "").trim();
      if (!id) return Promise.resolve(null);
      this.statusRequestLedgerRowId = id;
      this.statusRequestSeq = (this.statusRequestSeq || 0) + 1;
      const requestSeq = this.statusRequestSeq;
      this.loading = true;
      this.error = null;
      return getCommercialTermsStampStatus(id)
        .then(result => {
          if (
            this.statusRequestSeq !== requestSeq
            || String(this.ledgerRowId || "").trim() !== id
          ) {
            return null;
          }
          this.loading = false;
          if (!result || result.ok === false) {
            this.status = result || null;
            this.error =
              (result && (result.error || result.detail || result.message)) ||
              "Unable to read commercial terms stamp status.";
            return null;
          }
          this.status = result;
          if (result.status !== "review_needed") {
            this.resetReviewForm();
          } else {
            const nextEvidenceKey = this.reviewEvidenceKey(result);
            if (
              this.attestedReviewEvidenceKey
              && nextEvidenceKey
              && this.attestedReviewEvidenceKey !== nextEvidenceKey
            ) {
              this.resetReviewForm();
            }
            this.attestedReviewEvidenceKey = nextEvidenceKey;
          }
          const softStatuses = [
            "error",
            "missing",
            "blocked",
            "incomplete",
            "review_needed"
          ];
          if (
            result.error &&
            softStatuses.indexOf(String(result.status || "")) >= 0
          ) {
            this.error = String(result.error);
          }
          if (result.status === "bound") {
            this.$emit("stamped", {
              contentHash: result.contentHash || result.snapshotHash || "",
              snapshotId: result.snapshotId || null,
              policyNumber: result.policyNumber || this.carrierNumber
            });
          }
          return result;
        })
        .catch(err => {
          if (
            this.statusRequestSeq !== requestSeq
            || String(this.ledgerRowId || "").trim() !== id
          ) {
            return null;
          }
          this.loading = false;
          const data = err && err.response && err.response.data;
          const body =
            data &&
            (data.error ||
              data.message ||
              (data.result && (data.result.error || data.result.message)));
          this.error =
            (body && String(body)) ||
            (err && err.message && String(err.message)) ||
            "Unable to read commercial terms stamp status.";
          return null;
        });
    },

    promptStamp: function() {
      if (!this.canStamp) return;
      const fuse = String(this.carrierNumber || "").trim();
      const cents =
        this.status && Number.isSafeInteger(Number(this.status.boundPremiumCents))
          ? Number(this.status.boundPremiumCents)
          : null;
      const moneyLine =
        cents != null
          ? `You are about to stamp a ${centsToMoneyLabel(cents)} annual premium.`
          : "Premium will be taken from the accepted quote on the server.";
      const message =
        `Stamp quoted terms under ${fuse}? ${moneyLine} ` +
        "These terms will be locked for the schedule. This does not Post to INSIGHT.";
      if (!window.confirm(message)) return;
      this.doStamp(null);
    },

    promptConfirmQuotedTerms: function() {
      if (!this.canConfirmStamp) return;
      const message =
        `You are about to stamp a ${this.quotedPremiumLabel} annual premium with a ` +
        `${this.quotedExcessLabel} excess. These terms will be locked for the schedule.`;
      if (!window.confirm(message)) return;
      this.doStamp(this.buildPricingDecision());
    },

    promptEarlyPolicyReconciliation: function() {
      if (
        !this.reconciliationRequired
        || !this.confirmedNoExternalBordereaux
        || this.busy
      ) {
        return;
      }
      const message =
        `Replace the early ${this.carrierNumber} system record and stamp ` +
        `${this.quotedPremiumLabel} with a ${this.quotedExcessLabel} excess? ` +
        "The old record will remain available for audit.";
      if (!window.confirm(message)) return;
      const id = String(this.ledgerRowId || "").trim();
      const confirmedBoundPremiumCents =
        this.status && Number.isSafeInteger(Number(this.status.boundPremiumCents))
          ? Number(this.status.boundPremiumCents)
          : null;
      if (!id || !Number.isSafeInteger(confirmedBoundPremiumCents)) return;
      const expectedHeadSnapshotId = String(
        (this.reconciliationRequired && this.reconciliationRequired.replacedHeadSnapshotId) || ""
      ).trim();
      const expectedHeadContentHash = String(
        (this.reconciliationRequired && this.reconciliationRequired.replacedHeadContentHash) || ""
      ).trim();
      if (!expectedHeadSnapshotId || !expectedHeadContentHash) {
        this.error =
          "Refresh Stamp status before replacing this early record. The refused head details are missing.";
        return;
      }
      this.reconciling = true;
      this.error = null;
      this.info = null;
      // Zoho stamp-attestation note (approval evidence) is minted and verified
      // on the server. Do not invent approvalReference in the browser payload.
      return reconcileEarlyPolicy(id, {
        confirmedBoundPremiumCents,
        confirmedNoExternalBordereaux: true,
        confirmedReplacement: true,
        expectedHeadSnapshotId,
        expectedHeadContentHash,
        pricingDecision: this.buildPricingDecision()
      }).then(result => {
        if (String(this.ledgerRowId || "").trim() !== id) return null;
        this.reconciling = false;
        if (!result || result.ok === false) {
          const raw =
            (result && (result.error || result.detail || result.message)) ||
            "Early policy replacement was refused.";
          this.error = mapStampCommercialTermsError(raw);
          return null;
        }
        this.info =
          "The early record was replaced and the accepted quoted terms were stamped.";
        this.resetReviewForm();
        this.$emit("stamped", {
          contentHash: result.contentHash || result.snapshotHash || "",
          snapshotId: result.snapshotId || null,
          policyNumber: result.policyNumber || this.carrierNumber
        });
        return this.loadStatus();
      }).catch(() => {
        if (String(this.ledgerRowId || "").trim() !== id) return null;
        this.reconciling = false;
        this.error = "Unable to replace this early policy record right now.";
        return null;
      });
    },

    doStamp: function(pricingDecision) {
      const id = String(this.ledgerRowId || "").trim();
      if (!id || this.busy) return;
      const confirmedBoundPremiumCents =
        this.status && Number.isSafeInteger(Number(this.status.boundPremiumCents))
          ? Number(this.status.boundPremiumCents)
          : null;
      if (
        !Number.isSafeInteger(confirmedBoundPremiumCents)
        || confirmedBoundPremiumCents <= 0
      ) {
        this.error =
          "Stamp needs the confirmed bound premium cents from status before writing Neon.";
        return Promise.resolve(null);
      }
      this.statusRequestLedgerRowId = id;
      this.stamping = true;
      this.error = null;
      this.info = null;
      const options = { confirmedBoundPremiumCents };
      if (pricingDecision) {
        options.pricingDecision = pricingDecision;
      }
      return stampCommercialTerms(id, options)
        .then(result => {
          if (String(this.ledgerRowId || "").trim() !== id) {
            return null;
          }
          this.stamping = false;
          if (!result || result.ok === false) {
            const raw =
              (result && (result.error || result.detail || result.message)) ||
              "Stamp commercial terms was refused.";
            this.error = mapStampCommercialTermsError(raw);
            if (
              result
              && (
                result.status === "early_policy_reconciliation_required"
                || result.code === "early_policy_reconciliation_required"
              )
            ) {
              this.reconciliationRequired = result.reconciliation || {};
            }
            return null;
          }
          this.info = pricingDecision
            ? "The quoted terms were stamped using a confirmed pricing reference."
            : (result.alreadyBound
              ? "Commercial terms were already bound for this Fuse policy and period."
              : "Quoted terms stamped. Schedule Generate can use this Fuse policy number.");
          this.resetReviewForm();
          this.$emit("stamped", {
            contentHash: result.contentHash || result.snapshotHash || "",
            snapshotId: result.snapshotId || null,
            policyNumber: result.policyNumber || this.carrierNumber
          });
          return this.loadStatus();
        })
        .catch(() => {
          if (String(this.ledgerRowId || "").trim() !== id) {
            return null;
          }
          this.stamping = false;
          this.error = "Unable to stamp commercial terms right now.";
          return null;
        });
    }
  }
};
</script>
